'use client';

import { type NodeProps, type Node } from '@xyflow/react';
import { Text } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { type CodeGraphWithRelations } from '@terra/shared-schemas-git';
import type { GraphNodeType } from './types';
import { getNodeColor, getNodeIcon } from './utils';
import { CustomNodeTechnologies } from './CustomNodeTechnologies';
import { useGraphFilters } from './GraphFiltersContext';
import { BaseGraphNode } from '../flow-graph-base/BaseGraphNode';

export type CustomNodeProps = {
  type: GraphNodeType;
  name: string;
  filePath: string;
  technologies: NonNullable<CodeGraphWithRelations['technologies']>;
  id: string;
  repositoryName?: string;
  startLine?: number;
  endLine?: number;
  repositoryInfo?: import('@terra/ui-design-system').RepositoryInfo;
};

export function CustomNode({
  data: { type, name, repositoryName, technologies, id },
}: NodeProps<Node<CustomNodeProps>>) {
  const { showTechnologies, showRepositories } = useGraphFilters();
  const NodeIcon = getNodeIcon(type);
  const nodeColor = getNodeColor(type);

  const hasRepositoryInfo = showRepositories && !!repositoryName;
  const hasTechnologies = showTechnologies && !!technologies.length;
  const hasExtraContent = hasRepositoryInfo || hasTechnologies;

  return (
    <BaseGraphNode
      id={id}
      name={name}
      nodeColor={nodeColor}
      NodeIcon={NodeIcon}
    >
      {hasExtraContent ? (
        <>
          {hasRepositoryInfo && (
            <Box mb={1.5}>
              <Text fontSize="sm" color="fg.muted">
                {repositoryName}
              </Text>
            </Box>
          )}
          {hasTechnologies && (
            <CustomNodeTechnologies technologies={technologies} />
          )}
        </>
      ) : undefined}
    </BaseGraphNode>
  );
}
